import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  Alert,
  Snackbar,
  Chip,
  Divider,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
  Tooltip,
  Container
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Folder as FolderIcon,
  Assignment as AssignmentIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useKeycloak } from '../context/KeycloakContext';
import registryService from '../services/registryService';
import RegistryForm from '../components/RegistryForm';
import RegistryTable from '../components/RegistryTable';

// Dati mock temporanei per quando l'API non è disponibile
const mockRegistries = [
  {
    id: 1,
    name: 'Tipi di Documento',
    description: 'Tipologie di documenti utilizzabili',
    entryCount: 5,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 2,
    name: 'Classificazione Privacy',
    description: 'Livelli di classificazione per la privacy',
    entryCount: 3,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 3,
    name: 'Dipartimenti',
    description: 'Dipartimenti aziendali',
    entryCount: 4,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

// Dati mock per le voci
const mockEntries = {
  1: [
    { code: 'DOC001', description: 'Fattura' },
    { code: 'DOC002', description: 'Contratto' },
    { code: 'DOC003', description: 'Preventivo' },
    { code: 'DOC004', description: 'Ordine' },
    { code: 'DOC005', description: 'Bolla di Consegna' }
  ],
  2: [
    { code: 'PRI001', description: 'Pubblico' },
    { code: 'PRI002', description: 'Interno' },
    { code: 'PRI003', description: 'Riservato' }
  ],
  3: [
    { code: 'DEP001', description: 'Amministrazione' },
    { code: 'DEP002', description: 'Commerciale' },
    { code: 'DEP003', description: 'Produzione' },
    { code: 'DEP004', description: 'IT' }
  ]
};

const Registries = () => {
  const { keycloak } = useKeycloak();
  const userInfo = keycloak.tokenParsed || {};
  const isAdmin = userInfo.realm_access?.roles?.includes('admin');
  
  // Stati per la lista delle anagrafiche
  const [registries, setRegistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingMockData, setUsingMockData] = useState(false);
  
  // Stati per il dialogo di anagrafica
  const [openRegistryDialog, setOpenRegistryDialog] = useState(false);
  const [currentRegistry, setCurrentRegistry] = useState(null);
  const [isNewRegistry, setIsNewRegistry] = useState(true);
  
  // Stati per il dialogo di voci nell'anagrafica
  const [openEntriesDialog, setOpenEntriesDialog] = useState(false);
  const [selectedRegistry, setSelectedRegistry] = useState(null);
  const [entries, setEntries] = useState([]);
  
  // Stati per la creazione/modifica di una voce
  const [openEntryDialog, setOpenEntryDialog] = useState(false);
  const [currentEntry, setCurrentEntry] = useState({ code: '', description: '' });
  const [isNewEntry, setIsNewEntry] = useState(true);
  
  // Stato per lo snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Stato per il menu contestuale
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRegistryForMenu, setSelectedRegistryForMenu] = useState(null);
  
  // Stati per la gestione della paginazione
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Caricamento iniziale delle anagrafiche
  useEffect(() => {
    fetchRegistries();
  
  }, []);
  
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchRegistries(newPage, rowsPerPage, searchTerm);
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchRegistries(0, newRowsPerPage, searchTerm);
  };

  const handleSearch = (searchTerm) => {
    setSearchTerm(searchTerm);
    fetchRegistries(0, rowsPerPage, searchTerm);
  };
  
  // Carica tutte le anagrafiche
  const fetchRegistries = async (page = 0, size = 10, search = '') => {
    try {
      setLoading(true);
      const response = await registryService.getAll(page, size, search);
      console.log('API response data:', response);
      
      // Verifica che i dati ricevuti siano un array
      if (Array.isArray(response.content)) {
        setRegistries(response.content);
        setTotalCount(response.totalElements);
        setUsingMockData(false);
      } else {
        console.warn('API response is not an array, using mock data');
        setRegistries(mockRegistries);
        setTotalCount(mockRegistries.length);
        setUsingMockData(true);
        setError('Il server API non è completamente implementato, visualizzazione dati dimostrativi');
      }
    } catch (err) {
      console.error('Error fetching registries:', err);
      setRegistries(mockRegistries); // Usa dati mock in caso di errore
      setTotalCount(mockRegistries.length);
      setUsingMockData(true);
      setError('Impossibile connettersi al server API, visualizzazione dati dimostrativi');
    } finally {
      setLoading(false);
    }
  };
  
  // Apertura del dialogo per creare una nuova anagrafica
  const handleOpenNewRegistryDialog = () => {
    setCurrentRegistry({
      name: '',
      description: '',
      entries: []
    });
    setIsNewRegistry(true);
    setOpenRegistryDialog(true);
  };
  
  // Apertura del dialogo per modificare un'anagrafica esistente
  const handleOpenEditRegistryDialog = (registry) => {
    setCurrentRegistry({ ...registry });
    setIsNewRegistry(false);
    setOpenRegistryDialog(true);
    setAnchorEl(null);
  };
  
  // Chiusura del dialogo dell'anagrafica
  const handleCloseRegistryDialog = () => {
    setOpenRegistryDialog(false);
    setCurrentRegistry(null);
  };
  
  // Salvataggio dell'anagrafica (creazione o modifica)
  const handleSaveRegistry = async () => {
    try {
      if (!currentRegistry.name) {
        setSnackbar({
          open: true,
          message: 'Il nome dell\'anagrafica è obbligatorio',
          severity: 'error'
        });
        return;
      }
      
      let savedRegistry;
      
      if (usingMockData) {
        // Simula il salvataggio con dati mock
        if (isNewRegistry) {
          savedRegistry = {
            ...currentRegistry,
            id: mockRegistries.length + 1,
            entryCount: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          mockRegistries.push(savedRegistry);
        } else {
          const index = mockRegistries.findIndex(r => r.id === currentRegistry.id);
          if (index !== -1) {
            savedRegistry = {
              ...mockRegistries[index],
              name: currentRegistry.name,
              description: currentRegistry.description,
              updatedAt: new Date().toISOString()
            };
            mockRegistries[index] = savedRegistry;
          }
        }
        
        setSnackbar({
          open: true,
          message: isNewRegistry ? 'Anagrafica creata con successo (modalità demo)' : 'Anagrafica aggiornata con successo (modalità demo)',
          severity: 'success'
        });
      } else {
        // Salvataggio reale tramite API
        if (isNewRegistry) {
          savedRegistry = await registryService.create(currentRegistry);
          setSnackbar({
            open: true,
            message: 'Anagrafica creata con successo',
            severity: 'success'
          });
        } else {
          savedRegistry = await registryService.update(currentRegistry.id, currentRegistry);
          setSnackbar({
            open: true,
            message: 'Anagrafica aggiornata con successo',
            severity: 'success'
          });
        }
      }
      
      fetchRegistries(); // Ricarica le anagrafiche per avere i dati aggiornati
      handleCloseRegistryDialog();
    } catch (err) {
      console.error('Error saving registry:', err);
      setSnackbar({
        open: true,
        message: 'Errore durante il salvataggio dell\'anagrafica',
        severity: 'error'
      });
    }
  };
  
  // Eliminazione di un'anagrafica
  const handleDeleteRegistry = async (registryId) => {
    try {
      if (usingMockData) {
        // Rimuovi l'anagrafica dai dati mock
        const index = mockRegistries.findIndex(r => r.id === registryId);
        if (index !== -1) {
          mockRegistries.splice(index, 1);
        }
        
        setSnackbar({
          open: true,
          message: 'Anagrafica eliminata con successo (modalità demo)',
          severity: 'success'
        });
      } else {
        await registryService.delete(registryId);
        setSnackbar({
          open: true,
          message: 'Anagrafica eliminata con successo',
          severity: 'success'
        });
      }
      
      setAnchorEl(null);
      fetchRegistries(); // Ricarica le anagrafiche per avere i dati aggiornati
    } catch (err) {
      console.error('Error deleting registry:', err);
      setSnackbar({
        open: true,
        message: 'Errore durante l\'eliminazione dell\'anagrafica',
        severity: 'error'
      });
    }
  };
  
  // Apertura del dialogo delle voci
  const handleOpenEntriesDialog = async (registry) => {
    try {
      setSelectedRegistry(registry);
      
      if (usingMockData) {
        // Usa dati mock per le voci
        setEntries(mockEntries[registry.id] || []);
      } else {
        // Carica tutte le voci dell'anagrafica dal database
        const entriesData = await registryService.getEntriesByRegistryId(registry.id);
        setEntries(entriesData);
      }
      
      setOpenEntriesDialog(true);
      setAnchorEl(null);
    } catch (err) {
      console.error(`Error fetching entries for registry ${registry.id}:`, err);
      // Fallback ai dati mock in caso di errore
      setEntries(mockEntries[registry.id] || []);
      setSnackbar({
        open: true,
        message: 'Errore durante il caricamento delle voci, visualizzazione dati dimostrativi',
        severity: 'warning'
      });
    }
  };
  
  // Chiusura del dialogo delle voci
  const handleCloseEntriesDialog = () => {
    setOpenEntriesDialog(false);
    setSelectedRegistry(null);
    setEntries([]);
  };
  
  // Apertura del dialogo per aggiungere una nuova voce
  const handleOpenNewEntryDialog = () => {
    setCurrentEntry({ code: '', description: '' });
    setIsNewEntry(true);
    setOpenEntryDialog(true);
  };
  
  // Apertura del dialogo per modificare una voce esistente
  const handleOpenEditEntryDialog = (entry) => {
    setCurrentEntry({ ...entry });
    setIsNewEntry(false);
    setOpenEntryDialog(true);
  };
  
  // Chiusura del dialogo della voce
  const handleCloseEntryDialog = () => {
    setOpenEntryDialog(false);
    setCurrentEntry({ code: '', description: '' });
  };
  
  // Salvataggio di una voce
  const handleSaveEntry = async () => {
    try {
      if (!currentEntry.code || !currentEntry.description) {
        setSnackbar({
          open: true,
          message: 'Codice e descrizione sono obbligatori',
          severity: 'error'
        });
        return;
      }
      
      // Controllo che il codice non sia già utilizzato (solo per nuove voci)
      if (isNewEntry && entries.some(entry => entry.code === currentEntry.code)) {
        setSnackbar({
          open: true,
          message: 'Il codice è già utilizzato in questa anagrafica',
          severity: 'error'
        });
        return;
      }
      
      if (usingMockData) {
        // Gestione dati mock
        if (!mockEntries[selectedRegistry.id]) {
          mockEntries[selectedRegistry.id] = [];
        }
        
        if (isNewEntry) {
          // Aggiungi nuova voce ai mock
          mockEntries[selectedRegistry.id].push(currentEntry);
          
          // Aggiorna il conteggio nell'anagrafica
          const registryIndex = mockRegistries.findIndex(r => r.id === selectedRegistry.id);
          if (registryIndex !== -1) {
            mockRegistries[registryIndex].entryCount += 1;
          }
          
          setSnackbar({
            open: true,
            message: 'Voce aggiunta con successo (modalità demo)',
            severity: 'success'
          });
        } else {
          // Aggiorna voce esistente nei mock
          const entryIndex = mockEntries[selectedRegistry.id].findIndex(e => e.code === currentEntry.code);
          if (entryIndex !== -1) {
            mockEntries[selectedRegistry.id][entryIndex] = currentEntry;
          }
          
          setSnackbar({
            open: true,
            message: 'Voce aggiornata con successo (modalità demo)',
            severity: 'success'
          });
        }
        
        // Aggiorna lo stato delle voci
        setEntries([...mockEntries[selectedRegistry.id]]);
      } else {
        // Chiamate API reali
        if (isNewEntry) {
          // Aggiungi nuova voce
          await registryService.addEntry(selectedRegistry.id, currentEntry);
          setSnackbar({
            open: true,
            message: 'Voce aggiunta con successo',
            severity: 'success'
          });
        } else {
          // Aggiorna voce esistente
          await registryService.updateEntry(selectedRegistry.id, currentEntry.code, currentEntry);
          setSnackbar({
            open: true,
            message: 'Voce aggiornata con successo',
            severity: 'success'
          });
        }
        
        // Ricarica le voci dell'anagrafica
        const updatedEntries = await registryService.getEntriesByRegistryId(selectedRegistry.id);
        setEntries(updatedEntries);
      }
      
      // Aggiorna anche la lista delle anagrafiche
      fetchRegistries();
      
      handleCloseEntryDialog();
    } catch (err) {
      console.error('Error saving entry:', err);
      setSnackbar({
        open: true,
        message: 'Errore durante il salvataggio della voce',
        severity: 'error'
      });
    }
  };
  
  // Eliminazione di una voce
  const handleDeleteEntry = async (entryCode) => {
    try {
      if (usingMockData) {
        // Rimuovi la voce dai dati mock
        if (mockEntries[selectedRegistry.id]) {
          const entryIndex = mockEntries[selectedRegistry.id].findIndex(e => e.code === entryCode);
          if (entryIndex !== -1) {
            mockEntries[selectedRegistry.id].splice(entryIndex, 1);
            
            // Aggiorna il conteggio nell'anagrafica
            const registryIndex = mockRegistries.findIndex(r => r.id === selectedRegistry.id);
            if (registryIndex !== -1 && mockRegistries[registryIndex].entryCount > 0) {
              mockRegistries[registryIndex].entryCount -= 1;
            }
          }
        }
        
        // Aggiorna lo stato delle voci
        setEntries([...mockEntries[selectedRegistry.id] || []]);
        
        setSnackbar({
          open: true,
          message: 'Voce eliminata con successo (modalità demo)',
          severity: 'success'
        });
      } else {
        await registryService.deleteEntry(selectedRegistry.id, entryCode);
        
        // Ricarica le voci dell'anagrafica
        const updatedEntries = await registryService.getEntriesByRegistryId(selectedRegistry.id);
        setEntries(updatedEntries);
        
        setSnackbar({
          open: true,
          message: 'Voce eliminata con successo',
          severity: 'success'
        });
      }
      
      // Aggiorna anche la lista delle anagrafiche
      fetchRegistries();
    } catch (err) {
      console.error('Error deleting entry:', err);
      setSnackbar({
        open: true,
        message: 'Errore durante l\'eliminazione della voce',
        severity: 'error'
      });
    }
  };
  
  // Gestione del menu contestuale
  const handleOpenMenu = (event, registry) => {
    setAnchorEl(event.currentTarget);
    setSelectedRegistryForMenu(registry);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRegistryForMenu(null);
  };
  
  // Chiusura dello snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography component="h1" variant="h5">
                Anagrafiche
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenNewRegistryDialog}
              >
                Nuova Anagrafica
              </Button>
            </Box>

            <RegistryTable
              schema={{
                properties: {
                  code: { name: 'Codice', type: 'string', visible_on_ui: true, searchable: true },
                  name: { name: 'Nome', type: 'string', visible_on_ui: true, searchable: true },
                  description: { name: 'Descrizione', type: 'string', visible_on_ui: true, searchable: true },
                  sourceType: { name: 'Tipo Sorgente', type: 'string', visible_on_ui: true, searchable: true },
                  isInternal: { name: 'Interna', type: 'boolean', visible_on_ui: true, searchable: true }
                }
              }}
              data={registries}
              totalCount={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onEdit={handleOpenEditRegistryDialog}
              onDelete={handleDeleteRegistry}
              onSearch={handleSearch}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Registry Dialog */}
      <Dialog
        open={openRegistryDialog}
        onClose={handleCloseRegistryDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isNewRegistry ? 'Nuova Anagrafica' : 'Modifica Anagrafica'}
        </DialogTitle>
        <DialogContent>
          <RegistryForm
            schema={{
              properties: {
                code: { name: 'Codice', type: 'string', required: true, visible_on_ui: true },
                name: { name: 'Nome', type: 'string', required: true, visible_on_ui: true },
                description: { name: 'Descrizione', type: 'text', visible_on_ui: true },
                sourceType: { 
                  name: 'Tipo Sorgente', 
                  type: 'string', 
                  required: true, 
                  visible_on_ui: true,
                  enum: ['REST_API', 'DATABASE', 'EXCEL']
                },
                sourceConfig: { 
                  name: 'Configurazione Sorgente', 
                  type: 'text', 
                  required: true, 
                  visible_on_ui: true 
                },
                isInternal: { 
                  name: 'Anagrafica Interna', 
                  type: 'boolean', 
                  visible_on_ui: true 
                }
              }
            }}
            data={currentRegistry}
            onChange={setCurrentRegistry}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegistryDialog}>Annulla</Button>
          <Button onClick={handleSaveRegistry} variant="contained" color="primary">
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Registries; 